import { useEffect, useState } from 'react';

import type { CategorySummary } from '@/api/client';
import { wikiApiClient } from '@/api/client';
import { useT } from '@/i18n/LocaleProvider';

export type CategoryIndexProps = {
  lang: string;
  onNavigate: (name: string) => void;
};

export function CategoryIndex({ lang, onNavigate }: CategoryIndexProps) {
  const t = useT();
  const [categories, setCategories] = useState<CategorySummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(null);

    wikiApiClient
      .listCategories(lang)
      .then((data) => {
        if (!cancelled) {
          setCategories(data);
          setIsLoading(false);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : t('categories_error_load'));
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [lang, t]);

  return (
    <article className="note page-index" data-testid="category-index">
      <header className="note-header">
        <div className="note-kicker">
          <span>{t('categories_index_kicker')}</span>
        </div>
        <h1>{t('categories_title')}</h1>
      </header>

      {isLoading ? <div className="empty-note">{t('categories_loading')}</div> : null}
      {!isLoading && error ? <div className="empty-note">{error}</div> : null}
      {!isLoading && !error && categories.length === 0 ? (
        <div className="empty-note">{t('categories_empty')}</div>
      ) : null}
      {!isLoading && !error && categories.length > 0 ? (
        <ul className="page-index-list">
          {categories.map((cat) => (
            <li key={cat.name}>
              <a
                href={`/categories/${encodeURIComponent(cat.name)}`}
                onClick={(event) => {
                  event.preventDefault();
                  onNavigate(cat.name);
                }}
              >
                <strong>{cat.name}</strong>
                <span>
                  {cat.count === 1
                    ? t('categories_page_count_one')
                    : t('categories_page_count_many', { count: String(cat.count) })}
                </span>
              </a>
            </li>
          ))}
        </ul>
      ) : null}
    </article>
  );
}
